import type { ComplianceGridFormValues } from "./compliance-grid.schema";

// Generic bool↔string coercion lives in the shared util; re-exported for
// backward-compat so existing imports inside this folder don't need to change.
export { boolToString, stringToBool } from "newui/utils/boolStringCoerce";

export const COMPLIANCE_OPTIONS = [
	{ label: "Compliant", value: "true" },
	{ label: "Non-Compliant", value: "false" },
];

export const EVALUATED_OPTIONS = [
	{ label: "Yes", value: "true" },
	{ label: "No", value: "false" },
];

type SupplierFormValue = ComplianceGridFormValues["suppliers"][number];

/**
 * Pill state shown on each supplier's accordion header.
 *   compliant     → green  (is_evaluated === true)
 *   non_compliant → red    (is_evaluated === false)
 *   pending       → yellow (undecided, but some question answers given)
 *   none          → grey   (nothing marked yet)
 */
export type CompliancePillState =
	| "compliant"
	| "non_compliant"
	| "pending"
	| "none";

export const COMPLIANCE_PILL_META: Record<
	CompliancePillState,
	{ label: string; className: string }
> = {
	compliant: { label: "Compliant", className: "bg-green-100 text-green-700" },
	non_compliant: { label: "Non-Compliant", className: "bg-red-100 text-red-700" },
	pending: { label: "Pending", className: "bg-yellow-100 text-yellow-800" },
	none: { label: "Not assessed", className: "bg-gray-100 text-gray-500" },
};

export const getCompliancePillState = (
	supplier: SupplierFormValue
): CompliancePillState => {
	if (supplier.is_evaluated === true) {
		return "compliant";
	}
	if (supplier.is_evaluated === false) {
		return "non_compliant";
	}
	const anyAnswered = supplier.answers.some(
		(a) => a.is_compliant !== null && a.is_compliant !== undefined
	);
	return anyAnswered ? "pending" : "none";
};

/** True once every supplier has an explicit Compliant/Non-Compliant decision. */
export const allSuppliersDecided = (
	suppliers: SupplierFormValue[]
): boolean =>
	suppliers.every(
		(s) => s.is_evaluated === true || s.is_evaluated === false
	);
